// The builder island's markup as a pure function of state. The Worker renders it with
// react-dom/server for the first paint and the browser hydrates the same tree, so it
// must stay free of DOM globals and anything that differs between the two. The listboxes
// and inline slots keep their own open and editing state, and render as native controls and
// plain highlights until `enhanced` turns on after mount.

import type { CSSProperties, ReactNode } from "react";
import {
  BUILDER_COPY,
  GATE_COPY,
  GATE_SENT_COPY,
  GOAL_HELPERS,
  fillCopy,
} from "../../shared/prompts/copy";
import {
  BUSINESS_TYPES,
  BUSINESS_TYPE_META,
  CATEGORIES,
  CATEGORY_META,
  PLATFORMS,
  PLATFORM_META,
  SLOT_META,
  fillTemplateParts,
  resolveTemplate,
  templatesInCategory,
  type BusinessType,
  type Category,
  type Platform,
} from "../../shared/prompts/templates";
import { ACCOUNT_COUNT_OPTIONS, ROLE_OPTIONS } from "../../shared/prompts/gate-options";
import { SECTION_ICONS, goalStyle } from "../../shared/prompts/theme";
import { PLATFORM_ICONS } from "../lib/icons";
import { DuoIcon } from "./DuoIcon";
import { BracketText, InlinePrompt } from "./InlinePrompt";
import { inputValue } from "../lib/dom";
import { Listbox, type ListboxOption } from "./Listbox";
import {
  currentTemplate,
  fillInputsFor,
  slotValueOf,
  type BuilderState,
  type ExtraSlot,
  type PageType,
} from "../lib/store";

export type CopyStatus = "idle" | "copied" | "failed";
export type TextField = "brand" | "audience" | "tone" | "pillars";
export type GateTextField = "email" | "role" | "accounts";
export type CopySurface = "builder" | "copybar";

export interface BuilderActions {
  setGoal(goal: Category): void;
  setPlatform(platform: Platform): void;
  setBusinessType(value: string): void;
  setText(field: TextField, value: string): void;
  setSlot(key: ExtraSlot, value: string): void;
  commitText(field: TextField | ExtraSlot): void;
  pickPrompt(id: string): void;
  tryAnother(): void;
  toggleMore(): void;
  /** Opens Personalise on the business type, for prompts with a version per business. */
  showBusinessType(): void;
  /** Brings the live prompt into view. The prompt already updates as inputs change. */
  generate(): void;
  copy(surface: CopySurface): void;
  openGate(): void;
  closeGate(): void;
  setGateField(field: GateTextField, value: string): void;
  setNewsletter(checked: boolean): void;
  checkEmail(): void;
  submitGate(): void;
  /** Back from "check your inbox" to the form, answers kept. */
  editEmail(): void;
  resend(): void;
}

export interface BuilderViewProps {
  /** A category page renders its own visible "builder-title" heading above the island. */
  page: PageType;
  state: BuilderState;
  actions: BuilderActions;
  copyStatus: CopyStatus;
  /** The Turnstile widget. Client only: the island passes it, the server render does not. */
  turnstile?: ReactNode;
  /** False while Turnstile is required and has not produced a token yet. */
  gateReady?: boolean;
  /** Seconds until the confirmation link can be sent again. */
  resendIn?: number;
  /** True once the island has mounted. False on the server and during hydration. */
  enhanced?: boolean;
  /** react-dom's flushSync, from the client-only island. Absent on the server. */
  flush?: (fn: () => void) => void;
}

// The Worker type-checks this file without the DOM lib, where React's element types are
// empty, so handlers read the one property they need through these and lib/dom.ts.
function checkedOf(event: { target: unknown }): boolean {
  return (event.target as { checked?: unknown }).checked === true;
}

function listPlatforms(platforms: readonly Platform[]): string {
  const labels = platforms.map((p) => PLATFORM_META[p].label);
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

function Field({
  id,
  label,
  helper,
  error,
  children,
  className,
}: {
  id: string;
  label: string;
  helper?: string;
  error?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
      {helper ? (
        <p id={`${id}-help`} className="mt-1.5 text-[13px] text-neutral-600">
          {helper}
        </p>
      ) : null}
      {error ? <FieldError id={id} message={error} /> : null}
    </div>
  );
}

function FieldError({ id, message }: { id: string; message: string }) {
  return (
    <p id={`${id}-error`} className="mt-1.5 flex gap-1.5 text-[13px] font-medium text-red-600">
      <i className="fa-regular fa-circle-exclamation mt-0.5" aria-hidden="true" />
      {message}
    </p>
  );
}

/** aria-describedby for a control with optional helper and error lines. */
function describedBy(id: string, helper: boolean, error: string | undefined): string | undefined {
  const ids = [helper ? `${id}-help` : null, error ? `${id}-error` : null].filter(Boolean);
  return ids.length > 0 ? ids.join(" ") : undefined;
}

function BusinessTypeSelect({
  id,
  value,
  onChange,
  describedBy,
  invalid,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  describedBy?: string;
  invalid?: boolean;
}) {
  return (
    <select
      className="field"
      id={id}
      value={value}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      onChange={(e) => onChange(inputValue(e))}
    >
      <option value="">{GATE_COPY.rolePlaceholder}</option>
      {BUSINESS_TYPES.map((type: BusinessType) => (
        <option key={type} value={type}>
          {BUSINESS_TYPE_META[type].label}
        </option>
      ))}
    </select>
  );
}

function PackStrip({
  brand,
  audience,
  platform,
  onOpen,
}: {
  brand: string;
  audience: string;
  platform: Platform;
  onOpen: () => void;
}) {
  const heading = brand
    ? fillCopy(BUILDER_COPY.nextHeadingWithBrand, { brand })
    : BUILDER_COPY.nextHeading;
  const body = fillCopy(BUILDER_COPY.nextBody, {
    audience: audience || SLOT_META.audience.placeholder,
    platform: PLATFORM_META[platform].label,
  });
  return (
    <div className="pb-block-blurple flex flex-col gap-5 px-5 py-6 sm:px-7 lg:flex-row lg:items-center lg:justify-between lg:px-9">
      <div className="min-w-0">
        <div aria-hidden="true" className="flex gap-[3px]">
          {Array.from({ length: 50 }, (_, i) => (
            <span
              key={i}
              className={`h-3.5 min-w-0 flex-[0_1_0.25rem] rounded-full ${i === 0 ? "bg-[#D5F870]" : "bg-purple-300"}`}
            />
          ))}
        </div>
        <h3 className="mt-3 text-lg font-bold tracking-[-0.01em]">{heading}</h3>
        <p className="mt-1 max-w-[62ch] text-[15px] leading-[1.55] text-pretty">{body}</p>
      </div>
      <button
        type="button"
        className="ts-cta ts-cta-invert shrink-0 max-lg:w-full"
        id="pb-gate-open"
        onClick={onOpen}
      >
        {BUILDER_COPY.nextButton}{" "}
        <span className="ts-cta-arrow" aria-hidden="true">
          →
        </span>
      </button>
    </div>
  );
}

function GatePanel({
  state,
  actions,
  turnstile,
  gateReady,
  resendIn,
}: {
  state: BuilderState;
  actions: BuilderActions;
  turnstile: ReactNode;
  gateReady: boolean;
  resendIn: number;
}) {
  const { gate, inputs, feedback } = state;
  const errors = feedback.fieldErrors;
  const brand = inputs.brand.trim() || SLOT_META.brand.placeholder;
  const bulletIcons = [SECTION_ICONS.gatePack, SECTION_ICONS.gateIdeas, SECTION_ICONS.gatePdf];
  return (
    <div id="gate" className="grid scroll-mt-24 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
      <div className="pb-block-blurple px-5 py-7 sm:px-7 sm:py-9 lg:px-9">
        <div className="flex items-start justify-between gap-4">
          <span className="pb-tile pb-tile-lg pb-tile-white" aria-hidden="true">
            <DuoIcon name={SECTION_ICONS.gate} />
          </span>
          <button
            type="button"
            className="btn btn-ghost-invert btn-sm -mt-1 -mr-2 shrink-0"
            onClick={actions.closeGate}
          >
            <i className="fa-regular fa-xmark" aria-hidden="true" />
            {GATE_COPY.close}
          </button>
        </div>
        <h3
          id="gate-title"
          className="mt-5 text-[26px] leading-[1.15] font-bold tracking-[-0.015em] text-balance sm:text-[30px]"
        >
          {fillCopy(GATE_COPY.headline, { brand })}
        </h3>
        <p className="mt-3 max-w-[48ch] text-[15px] leading-[1.6]">{GATE_COPY.sub}</p>
        <ul role="list" className="mt-6 flex flex-col gap-3.5 text-[15px] leading-[1.45]">
          {GATE_COPY.bullets.map((bullet, i) => (
            <li key={bullet} className="flex gap-3">
              <DuoIcon name={bulletIcons[i]} className="mt-0.5 w-5 text-[#D5F870]" />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="bg-white px-5 py-7 sm:px-7 sm:py-9 lg:px-9">
        {feedback.sentTo ? (
          <SentPanel
            state={state}
            actions={actions}
            turnstile={turnstile}
            gateReady={gateReady}
            resendIn={resendIn}
          />
        ) : (
          <form
            className="grid gap-5"
            noValidate
            aria-labelledby="gate-title"
            aria-busy={feedback.submitting || undefined}
            onSubmit={(e) => {
              e.preventDefault();
              actions.submitGate();
            }}
          >
            <div>
              <label className="label" htmlFor="g-email">
                {GATE_COPY.emailLabel}{" "}
                <span className="text-red-500" aria-hidden="true">
                  *
                </span>
              </label>
              <input
                className="field mt-1.5"
                id="g-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder={GATE_COPY.emailPlaceholder}
                value={gate.email}
                aria-invalid={errors.email ? true : undefined}
                aria-describedby={
                  [errors.email ? "g-email-error" : null, feedback.nudge ? "g-email-nudge" : null]
                    .filter(Boolean)
                    .join(" ") || undefined
                }
                onChange={(e) => actions.setGateField("email", inputValue(e))}
                onBlur={actions.checkEmail}
              />
              {errors.email ? <FieldError id="g-email" message={errors.email} /> : null}
              <p
                id="g-email-nudge"
                aria-live="polite"
                className={
                  feedback.nudge && !errors.email
                    ? "mt-1.5 text-[13px] text-neutral-700"
                    : "sr-only"
                }
              >
                {feedback.nudge && !errors.email ? GATE_COPY.freemailNudge : null}
              </p>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <Field id="g-role" label={GATE_COPY.roleLabel} error={errors.role}>
                <select
                  className="field"
                  id="g-role"
                  name="role"
                  required
                  value={gate.role}
                  aria-invalid={errors.role ? true : undefined}
                  aria-describedby={describedBy("g-role", false, errors.role)}
                  onChange={(e) => actions.setGateField("role", inputValue(e))}
                >
                  <option value="">{GATE_COPY.rolePlaceholder}</option>
                  {ROLE_OPTIONS.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field id="g-biz" label={GATE_COPY.businessTypeLabel} error={errors.businessType}>
                <BusinessTypeSelect
                  id="g-biz"
                  value={inputs.businessType}
                  onChange={actions.setBusinessType}
                  invalid={!!errors.businessType}
                  describedBy={describedBy("g-biz", false, errors.businessType)}
                />
              </Field>
            </div>

            <Field
              id="g-accounts"
              label={GATE_COPY.accountsLabel}
              helper={GATE_COPY.accountsHelper}
              error={errors.accounts}
            >
              <select
                className="field"
                id="g-accounts"
                name="accounts"
                required
                aria-invalid={errors.accounts ? true : undefined}
                aria-describedby={describedBy("g-accounts", true, errors.accounts)}
                value={gate.accounts}
                onChange={(e) => actions.setGateField("accounts", inputValue(e))}
              >
                <option value="">{GATE_COPY.rolePlaceholder}</option>
                {ACCOUNT_COUNT_OPTIONS.map((band) => (
                  <option key={band.id} value={band.id}>
                    {band.label}
                  </option>
                ))}
              </select>
            </Field>

            <label
              className="flex items-start gap-3 text-[15px] leading-[1.45] text-neutral-800 sm:text-sm"
              htmlFor="g-news"
            >
              <span className="flex h-lh shrink-0 items-center">
                <span className="group inline-grid size-5 grid-cols-1 sm:size-4">
                  <input
                    id="g-news"
                    name="newsletter"
                    type="checkbox"
                    checked={gate.newsletter}
                    onChange={(e) => actions.setNewsletter(checkedOf(e))}
                    className="col-start-1 row-start-1 appearance-none rounded-sm border border-neutral-300 bg-white checked:border-purple-500 checked:bg-purple-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500 forced-colors:appearance-auto"
                  />
                  <svg
                    viewBox="0 0 14 14"
                    fill="none"
                    aria-hidden="true"
                    className="pointer-events-none col-start-1 row-start-1 size-7/8 self-center justify-self-center stroke-white"
                  >
                    <path
                      d="M3 8L6 11L11 3.5"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="group-not-has-checked:opacity-0"
                    />
                  </svg>
                </span>
              </span>
              <span>
                {GATE_COPY.newsletterLabel}
                <span className="mt-0.5 block text-[13px] text-neutral-600">
                  {GATE_COPY.newsletterHelper}
                </span>
              </span>
            </label>

            {turnstile}

            <div className="flex flex-col gap-3">
              {feedback.formError ? (
                <p
                  role="alert"
                  className="flex gap-2 rounded-lg bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700 ring-1 ring-red-200"
                >
                  <i className="fa-regular fa-circle-exclamation mt-0.5" aria-hidden="true" />
                  {feedback.formError}
                </p>
              ) : null}
              <button
                type="submit"
                className="ts-cta ts-cta-lg sm:self-start max-sm:w-full"
                disabled={feedback.submitting || !gateReady}
              >
                {feedback.submitting ? (
                  <>
                    <i className="fa-regular fa-spinner-third animate-spin" aria-hidden="true" />{" "}
                    {GATE_COPY.loading}
                  </>
                ) : (
                  <>
                    {GATE_COPY.button}{" "}
                    <span className="ts-cta-arrow" aria-hidden="true">
                      →
                    </span>
                  </>
                )}
              </button>
              <p className="max-w-[60ch] text-[13px] leading-[1.55] text-neutral-600">
                {GATE_COPY.privacyLine}{" "}
                <a
                  className="font-semibold text-purple-600 underline underline-offset-2 hover:text-purple-700"
                  href="https://skedsocial.com/privacy-policy"
                >
                  {GATE_COPY.privacyLink}
                </a>
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function SentPanel({
  state,
  actions,
  turnstile,
  gateReady,
  resendIn,
}: {
  state: BuilderState;
  actions: BuilderActions;
  turnstile: ReactNode;
  gateReady: boolean;
  resendIn: number;
}) {
  const { feedback } = state;
  const brand = state.inputs.brand.trim() || SLOT_META.brand.placeholder;
  const email = feedback.sentTo ?? "";
  return (
    <div className="grid content-start gap-5">
      <div role="status" className="rounded-[16px] bg-white p-5 ring-1 ring-neutral-950/5 sm:p-6">
        <h4
          id="gate-sent-title"
          tabIndex={-1}
          className="flex items-center gap-2.5 text-xl font-bold tracking-[-0.01em] text-ink focus:outline-none"
        >
          <i className="fa-regular fa-envelope-open-text text-purple-600" aria-hidden="true" />
          {GATE_SENT_COPY.headline}
        </h4>
        <p className="mt-2 text-[15px] leading-[1.6] text-neutral-800">
          {fillCopy(GATE_SENT_COPY.body, { email, brand })}
        </p>
        <p className="mt-2 text-[13px] leading-[1.55] text-neutral-600">{GATE_SENT_COPY.help}</p>
        {feedback.resent ? (
          <p className="mt-3 text-[13px] font-medium text-purple-700">
            {fillCopy(GATE_SENT_COPY.resent, { email })}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        className="btn btn-ghost btn-sm -ml-2 self-start"
        onClick={actions.editEmail}
      >
        <i className="fa-regular fa-pen" aria-hidden="true" />
        {GATE_SENT_COPY.edit}
      </button>
      {turnstile}
      {feedback.formError ? (
        <p
          role="alert"
          className="flex gap-2 rounded-lg bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700 ring-1 ring-red-200"
        >
          <i className="fa-regular fa-circle-exclamation mt-0.5" aria-hidden="true" />
          {feedback.formError}
        </p>
      ) : null}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
        <button
          type="button"
          className="btn btn-outline self-start"
          disabled={feedback.submitting || resendIn > 0 || !gateReady}
          onClick={actions.resend}
        >
          <i
            className={
              feedback.submitting
                ? "fa-regular fa-spinner-third animate-spin"
                : "fa-regular fa-paper-plane"
            }
            aria-hidden="true"
          />
          {GATE_SENT_COPY.resend}
        </button>
        {resendIn > 0 ? (
          <p className="text-[13px] text-neutral-600">
            {fillCopy(GATE_SENT_COPY.resendWait, { seconds: String(resendIn) })}
          </p>
        ) : null}
      </div>
    </div>
  );
}

const GOAL_OPTIONS: ListboxOption<Category>[] = CATEGORIES.map((goal) => ({
  value: goal,
  label: CATEGORY_META[goal].name,
  detail: GOAL_HELPERS[goal],
  lead: (
    <span className="pb-tile pb-tile-sm pb-tile-solid" style={goalStyle(goal) as CSSProperties}>
      <DuoIcon name={CATEGORY_META[goal].icon} />
    </span>
  ),
}));

const PLATFORM_OPTIONS: ListboxOption<Platform>[] = PLATFORMS.map((platform) => ({
  value: platform,
  label: PLATFORM_META[platform].label,
  lead: (
    <span className="pb-tile pb-tile-sm pb-tile-neutral">
      <i className={PLATFORM_ICONS[platform]} />
    </span>
  ),
}));

function promptOptions(goal: Category): ListboxOption<string>[] {
  return templatesInCategory(goal).map((t) => ({
    value: t.id,
    label: t.title,
    lead: (
      <span className="pb-tile pb-tile-sm" style={goalStyle(t.category) as CSSProperties}>
        <DuoIcon name={t.icon} />
      </span>
    ),
  }));
}

export function BuilderView({
  page,
  state,
  actions,
  copyStatus,
  turnstile = null,
  gateReady = true,
  resendIn = 0,
  enhanced = false,
  flush,
}: BuilderViewProps) {
  const { inputs } = state;
  const template = currentTemplate(inputs);
  const resolved = resolveTemplate(template, inputs.businessType || undefined);
  const parts = fillTemplateParts(template, fillInputsFor(inputs));
  const mismatch = !resolved.platforms.includes(inputs.platform);
  const brand = inputs.brand.trim();
  const showVariantHint = !inputs.businessType && Object.keys(template.variants ?? {}).length > 0;

  return (
    <section
      id="builder"
      aria-labelledby="builder-title"
      className="scroll-mt-24 overflow-hidden rounded-[20px] border border-neutral-950/5 bg-white shadow-[0_28px_60px_-18px_rgba(16,24,40,0.18),0_2px_4px_rgba(16,24,40,0.04)]"
    >
      {page === "hub" ? (
        <h2 id="builder-title" className="sr-only">
          {BUILDER_COPY.heading}
        </h2>
      ) : null}

      <form
        id="pb-form"
        method="get"
        action="#builder"
        className="border-b border-neutral-100 bg-neutral-25 p-5 sm:p-7"
        onSubmit={(e) => {
          e.preventDefault();
          actions.generate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] lg:items-end">
          <Listbox
            id="pb-goal"
            name="goal"
            label={BUILDER_COPY.goalLabel}
            value={inputs.goal}
            options={GOAL_OPTIONS}
            onChange={actions.setGoal}
            enhanced={enhanced}
          />
          <Listbox
            id="pb-platform"
            name="platform"
            label={BUILDER_COPY.platformLabel}
            value={inputs.platform}
            options={PLATFORM_OPTIONS}
            onChange={actions.setPlatform}
            enhanced={enhanced}
          />
          <button type="submit" className="ts-cta ts-cta-lg sm:col-span-2 lg:col-span-1">
            {BUILDER_COPY.generate}
          </button>
        </div>
      </form>

      <div id="pb-result" className="scroll-mt-24 p-5 sm:p-7">
        <div className="flex flex-wrap items-end gap-3">
          <Listbox
            id="pb-prompt"
            name="prompt"
            form="pb-form"
            label={BUILDER_COPY.pickerLabel}
            value={template.id}
            options={promptOptions(inputs.goal)}
            onChange={actions.pickPrompt}
            enhanced={enhanced}
            className="min-w-0 flex-[1_1_16rem]"
          />
          <button
            type="button"
            className="js-only btn btn-outline min-h-13 max-sm:flex-1"
            onClick={actions.tryAnother}
          >
            <i className="fa-regular fa-shuffle" aria-hidden="true" />
            {BUILDER_COPY.tryAnother}
          </button>
        </div>

        <div className="mt-5 flex flex-col gap-1 text-[13px] text-neutral-600">
          <p className="js-only">
            <BracketText text={BUILDER_COPY.inlineHint} gapClass="gap" />
          </p>
          {mismatch ? (
            <p>
              {fillCopy(BUILDER_COPY.platformMismatch, {
                platforms: listPlatforms(resolved.platforms),
                platform: PLATFORM_META[inputs.platform].label,
              })}
            </p>
          ) : null}
        </div>
        <div
          id="pb-out"
          tabIndex={-1}
          style={goalStyle(template.category) as CSSProperties}
          className="pb-out pb-band mt-3 rounded-2xl px-5 py-5 text-[17px] leading-[1.8] whitespace-pre-line text-pretty wrap-break-word text-neutral-900 sm:px-7 sm:py-6 sm:text-[19px]"
        >
          <InlinePrompt
            parts={parts}
            valueOf={(slot) => slotValueOf(inputs, slot)}
            onEdit={(slot, value) =>
              slot === "brand" || slot === "audience"
                ? actions.setText(slot, value)
                : actions.setSlot(slot, value)
            }
            onCommit={actions.commitText}
            flush={flush}
            enhanced={enhanced}
          />
        </div>

        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
          <button
            type="button"
            className="js-only btn btn-primary max-sm:w-full"
            data-state={copyStatus === "copied" ? "copied" : undefined}
            onClick={() => actions.copy("builder")}
          >
            <i
              className={copyStatus === "copied" ? "fa-solid fa-check" : "fa-regular fa-copy"}
              aria-hidden="true"
            />
            {copyStatus === "copied" ? BUILDER_COPY.copied : BUILDER_COPY.copy}
          </button>
          <p className="pr-16 text-[13px] leading-[1.5] text-neutral-600">
            {BUILDER_COPY.resultHelper}
          </p>
        </div>
        <p
          role="status"
          className={copyStatus === "failed" ? "mt-2 text-[13px] text-red-600" : "sr-only"}
        >
          {copyStatus === "failed" ? BUILDER_COPY.copyFailed : null}
          {copyStatus === "copied" ? BUILDER_COPY.copied : null}
        </p>

        <div className="mt-6 border-t border-neutral-100 pt-5">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <button
              type="button"
              className="inline-flex cursor-pointer items-center gap-2 rounded-md text-sm font-semibold text-purple-600 hover:text-purple-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500"
              aria-expanded={state.showMore}
              aria-controls="pb-more"
              onClick={actions.toggleMore}
            >
              <i className="fa-regular fa-sliders" aria-hidden="true" />
              {BUILDER_COPY.personalise}
              <i
                className={`fa-regular fa-chevron-down text-xs transition-transform duration-200 ${state.showMore ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </button>
            {showVariantHint ? (
              <p className="text-[13px] text-neutral-600">
                {BUILDER_COPY.variantHint}{" "}
                <button
                  type="button"
                  className="cursor-pointer font-semibold text-purple-600 underline underline-offset-2 hover:text-purple-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500"
                  onClick={actions.showBusinessType}
                >
                  {BUILDER_COPY.variantAction}
                </button>
              </p>
            ) : null}
          </div>
          <div id="pb-more" hidden={!state.showMore} className="mt-4 grid gap-4 sm:grid-cols-3">
            <Field
              id="pb-biz"
              label={BUILDER_COPY.businessTypeLabel}
              helper={BUILDER_COPY.businessTypeHelper}
            >
              <BusinessTypeSelect
                id="pb-biz"
                value={inputs.businessType}
                describedBy="pb-biz-help"
                onChange={actions.setBusinessType}
              />
            </Field>
            <Field id="pb-tone" label={BUILDER_COPY.toneLabel} helper={BUILDER_COPY.toneHelper}>
              <input
                className="field"
                id="pb-tone"
                aria-describedby="pb-tone-help"
                placeholder={BUILDER_COPY.tonePlaceholder}
                value={inputs.tone}
                onChange={(e) => actions.setText("tone", inputValue(e))}
                onBlur={() => actions.commitText("tone")}
              />
            </Field>
            <Field
              id="pb-pillars"
              label={BUILDER_COPY.pillarsLabel}
              helper={BUILDER_COPY.pillarsHelper}
            >
              <input
                className="field"
                id="pb-pillars"
                aria-describedby="pb-pillars-help"
                placeholder={BUILDER_COPY.pillarsPlaceholder}
                value={inputs.pillars}
                onChange={(e) => actions.setText("pillars", inputValue(e))}
                onBlur={() => actions.commitText("pillars")}
              />
            </Field>
          </div>
        </div>
      </div>

      <div className="pb-g">
        {state.gateOpen ? (
          <GatePanel
            state={state}
            actions={actions}
            turnstile={turnstile}
            gateReady={gateReady}
            resendIn={resendIn}
          />
        ) : (
          <PackStrip
            brand={brand}
            audience={inputs.audience.trim()}
            platform={inputs.platform}
            onOpen={actions.openGate}
          />
        )}
      </div>
    </section>
  );
}

/**
 * The mobile copy bar. BuilderIsland portals it to <body>: the island sits inside an
 * animated (transformed) wrapper, which would pin a fixed bar to the builder card instead
 * of the viewport.
 */
export function CopyBar({
  title,
  visible,
  copyStatus,
  onCopy,
}: {
  title: string;
  visible: boolean;
  copyStatus: CopyStatus;
  onCopy: () => void;
}) {
  return (
    <div
      className="copybar fixed inset-x-0 bottom-0 z-40 border-t border-neutral-950/10 bg-white px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-8px_24px_-12px_rgba(16,24,40,0.18)] lg:hidden"
      data-show={visible ? "1" : undefined}
      aria-hidden={!visible}
    >
      <div className="flex items-center gap-3">
        <p className="min-w-0 flex-1 text-sm leading-tight">
          <span className="block text-[11px] font-bold tracking-[0.08em] text-neutral-500 uppercase">
            {BUILDER_COPY.resultHeading}
          </span>
          <span className="block truncate font-semibold text-ink">{title}</span>
        </p>
        <button
          type="button"
          className="btn btn-primary"
          tabIndex={visible ? undefined : -1}
          data-state={copyStatus === "copied" ? "copied" : undefined}
          onClick={onCopy}
        >
          <i
            className={copyStatus === "copied" ? "fa-solid fa-check" : "fa-regular fa-copy"}
            aria-hidden="true"
          />
          {copyStatus === "copied" ? BUILDER_COPY.copied : BUILDER_COPY.copy}
        </button>
      </div>
    </div>
  );
}
